import React, { useRef, useState, useEffect } from 'react';
import { Printer, Edit } from 'lucide-react';
import apiService from '../../../config';

// Static row labels (2nd column — never changes)
const PARTICULARS = [
  "Order No. & date under which work originally be sanctioned.",
  "In case of the transferred work the office from whom the work in transferred any place continued.",
  "Present stage of the work.",
  "Land actually if already taken over by private negotiation date of taking over possession of land.",
  "If land rent had been sanctioned so for details for the",
  "Panchnama of standing crops, trees, etc. if any held at the construction and action taken their upon for compensation etc.",
  "Funds provision of funds towards compensation payment.",
  "Remark."
];

// API field keys for rows (null = handled separately)
const API_FIELD_KEYS = [
  null,                          // row 0 → order_no + date (handled separately)
  'transferred_from_office',
  'present_work_stage',
  null,                          // row 3 → is_possession_taken_private + possession_taken_date (handled separately)
  'land_rent_details',
  'panchnama_details',
  'compensation_fund_details',
  'remarks'
];

const EMPTY_ROWS = Array(8).fill('');

// ── Date utils ─────────────────────────────────────────────────────────────
// Convert any date string/ISO from API → "YYYY-MM-DD" for <input type="date">
const toInputDate = (val) => {
  if (!val || val === '' || val === null) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(val))) return val;
  if (String(val).length >= 10) return String(val).substring(0, 10);
  return '';
};

// Convert "YYYY-MM-DD" input → full ISO string (backend expects this format)
const toISOForSQL = (val) => {
  if (!val || val === '') return null;
  try {
    return new Date(val).toISOString();
  } catch { return null; }
};

// Display format for document preview → "DD/MM/YYYY"
const toDisplayDate = (val) => {
  if (!val || val === '') return '';
  try {
    const parts = String(val).substring(0, 10).split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return val;
  } catch { return val; }
};

const AnnexureE = () => {
  const documentRef = useRef(null);

  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentProposalId, setCurrentProposalId] = useState(null);

  // Display data
  const [workName, setWorkName] = useState('');         // header col3 — static display only
  const [rowDetails, setRowDetails] = useState([...EMPTY_ROWS]);
  const [orderNo, setOrderNo] = useState('');
  const [orderDate, setOrderDate] = useState('');
  const [possessionText, setPossessionText] = useState('');  // row 4 — is_possession_taken_private
  const [possessionDate, setPossessionDate] = useState('');  // row 4 — possession_taken_date
  const [apiWorkId, setApiWorkId] = useState(null);

  // Edit form state
  const [isEditing, setIsEditing] = useState(false);
  const [editDetails, setEditDetails] = useState([...EMPTY_ROWS]);
  const [editOrderNo, setEditOrderNo] = useState('');
  const [editOrderDate, setEditOrderDate] = useState('');
  const [editPossessionText, setEditPossessionText] = useState('');
  const [editPossessionDate, setEditPossessionDate] = useState('');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ── Load project ID ────────────────────────────────────────────────────
  useEffect(() => {
    const getProjectInfo = () => {
      for (const key of ['currentProjectData', 'selectedProject']) {
        const raw = localStorage.getItem(key);
        if (raw) {
          try {
            const d = JSON.parse(raw);
            const id = d.project_id || d.id;
            if (id) return { id };
          } catch { }
        }
      }
      try {
        const u = JSON.parse(localStorage.getItem('userData') || '{}');
        return { id: u.data?.project_id || u.project_id || null };
      } catch { }
      return { id: null };
    };
    setCurrentProjectId(getProjectInfo().id);
  }, []);

  // ── Load proposal ID ───────────────────────────────────────────────────
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('userData') || '{}');
      const id = u?.data?.default_proposal || u?.data?.proposal_id ||
        u?.default_proposal || u?.proposal_id || null;
      setCurrentProposalId(id);
    } catch { }
  }, []);

  // ── Fetch API data ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentProjectId || !currentProposalId) return;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiService.getRailwayEWorkDetails(currentProjectId, currentProposalId);
        const raw = response?.data || response;
        const data = Array.isArray(raw) ? raw[0] : raw;

        if (data && Object.keys(data).length > 0) {
          // header col3 — static display only
          setWorkName(data.work_name ?? '');
          setApiWorkId(data.work_id ?? null);

          // row 1 — two fields
          const on = data.order_no ?? '';
          const od = toInputDate(data.date ?? '');
          setOrderNo(on);
          setOrderDate(od);
          setEditOrderNo(on);
          setEditOrderDate(od);

          // row 4 — possession text + date
          const pt = data.is_possession_taken_private ?? '';
          const pd = toInputDate(data.possession_taken_date ?? '');
          setPossessionText(pt);
          setPossessionDate(pd);
          setEditPossessionText(pt);
          setEditPossessionDate(pd);

          // rows 2–8
          const mapped = API_FIELD_KEYS.map((k, idx) =>
            idx === 0 ? '' : (data[k] ?? '')
          );
          setRowDetails(mapped);
          setEditDetails(mapped);
        }
      } catch (err) {
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentProjectId, currentProposalId]);

  // ── Helpers ────────────────────────────────────────────────────────────
  const openEdit = () => {
    setEditOrderNo(orderNo);
    setEditOrderDate(orderDate);
    setEditPossessionText(possessionText);
    setEditPossessionDate(possessionDate);
    setEditDetails([...rowDetails]);
    setError(null);
    setIsEditing(true);
  };

  const closeEdit = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleDetailChange = (idx, value) => {
    const updated = [...editDetails];
    updated[idx] = value;
    setEditDetails(updated);
  };

  // ── Save ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        project_id: currentProjectId,
        proposal_id: currentProposalId,
        order_no: editOrderNo,
        date: toISOForSQL(editOrderDate),
        is_possession_taken_private: editPossessionText,
        possession_taken_date: toISOForSQL(editPossessionDate),
      };
      API_FIELD_KEYS.forEach((key, idx) => {
        if (idx !== 0 && idx !== 3 && key) payload[key] = editDetails[idx];
      });

      await apiService.postAddUpdateEWorkDetails(payload);

      setOrderNo(editOrderNo);
      setOrderDate(editOrderDate);
      setPossessionText(editPossessionText);
      setPossessionDate(editPossessionDate);
      setRowDetails([...editDetails]);
      setIsEditing(false);
    } catch (err) {
      console.error('Save error:', err);
      setError(err?.response?.data?.message || err.message || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Print ──────────────────────────────────────────────────────────────
  const handlePrint = () => {
    const printContent = documentRef.current?.innerHTML;
    if (!printContent) { alert('No content to print.'); return; }
    const win = window.open('', '', 'height=900,width=1200');
    win.document.write(`
      <html>
        <head>
          <title>Annexure E</title>
          <style>
            @page { size: A4 portrait; margin: 0.3in 0.6in 0.3in 0.75in; }
            body { font-family: 'Noto Sans Devanagari','Mangal','Arial',sans-serif; font-size: 12px; line-height: 1.4; color: #000; margin: 0; padding: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #000; padding: 8px; text-align: left; vertical-align: top; }
            th { background-color: #d3d3d3; font-weight: bold; }
            .header { text-align: center; margin-bottom: 10px; font-weight: bold; font-size: 15px; }
            .sign { text-align: center; margin-top: 40px; }
            .two-fields { display: flex; gap: 12px; }
            .two-fields span { flex: 1; }
            @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    win.document.close();
    win.print();
  };

  // ── Row 1 display (order_no + date combined) ───────────────────────────
  const renderRow1Display = () => {
    if (!orderNo && !orderDate) return '';
    const parts = [];
    if (orderNo) parts.push(`No: ${orderNo}`);
    if (orderDate) parts.push(`Date: ${orderDate}`);
    return parts.join('  |  ');
  };

  // ── Edit Modal ─────────────────────────────────────────────────────────
  const renderEditModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">

          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Annexure E - Edit</h2>
            <button onClick={closeEdit} className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none">×</button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
          )}

          {/* Section Pill */}
          <div className="flex items-center justify-center mb-6">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-full">
              <h2 className="font-bold text-lg">Work Details</h2>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="overflow-x-auto rounded-xl border border-gray-200 mb-4">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-200 px-3 py-3 text-center text-gray-600 font-semibold w-12">No.</th>
                    <th className="border border-gray-200 px-4 py-3 text-left text-gray-600 font-semibold" style={{ width: '38%' }}>
                      Name of the work
                    </th>
                    {/* Header col3 — static, not editable */}
                    <th className="border border-gray-200 px-4 py-3 text-left text-gray-700 font-semibold bg-gray-50">
                      {workName || 'Proposed gauge conversion from MG to BG between KHANDWA-AKOLA station.'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* ── Row 1 — TWO FIELDS: order_no + date ── */}
                  <tr className="bg-white">
                    <td className="border border-gray-200 px-3 py-3 text-center text-gray-500 align-top font-medium w-12">1</td>
                    <td className="border border-gray-200 px-4 py-3 text-gray-700 align-top" style={{ width: '38%' }}>
                      {PARTICULARS[0]}
                    </td>
                    <td className="border border-gray-200 px-3 py-3 align-top">
                      <div className="flex flex-col gap-3">
                        {/* Order Number */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">
                            Order No.
                          </label>
                          <input
                            type="text"
                            value={editOrderNo}
                            onChange={e => setEditOrderNo(e.target.value)}
                            placeholder="Enter order number..."
                            className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all text-gray-800"
                            style={{ fontSize: '13px' }}
                          />
                        </div>
                        {/* Date */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">
                            Date
                          </label>
                          <input
                            type="date"
                            value={editOrderDate}
                            onChange={e => setEditOrderDate(e.target.value)}
                            className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all text-gray-800"
                            style={{ fontSize: '13px' }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>

                  {/* ── Rows 2–8 — row 4 (idx=3) has text+date, others textarea ── */}
                  {PARTICULARS.slice(1).map((label, i) => {
                    const idx = i + 1;
                    return (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}>
                        <td className="border border-gray-200 px-3 py-2 text-center text-gray-500 align-top font-medium w-12">
                          {idx + 1}
                        </td>
                        <td className="border border-gray-200 px-4 py-2 text-gray-700 align-top" style={{ width: '38%' }}>
                          {label}
                        </td>
                        <td className="border border-gray-200 px-3 py-2 align-top">
                          {idx === 3 ? (
                            /* Row 4 — Yes/No toggle + date only if Yes */
                            <div className="flex flex-col gap-3">
                              <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                                  Land Taken Over by Private Negotiation?
                                </label>
                                <div className="flex gap-3">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditPossessionText('1');
                                    }}
                                    className={`px-5 py-2 rounded-lg font-semibold text-sm border-2 transition-all ${String(editPossessionText) === '1'
                                      ? 'bg-green-500 border-green-500 text-white shadow'
                                      : 'bg-white border-gray-300 text-gray-600 hover:border-green-400'
                                      }`}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditPossessionText('0');
                                      setEditPossessionDate(''); // clear date on No
                                    }}
                                    className={`px-5 py-2 rounded-lg font-semibold text-sm border-2 transition-all ${String(editPossessionText) === '0'
                                      ? 'bg-red-500 border-red-500 text-white shadow'
                                      : 'bg-white border-gray-300 text-gray-600 hover:border-red-400'
                                      }`}
                                  >
                                    No
                                  </button>
                                </div>
                              </div>
                              {/* Date — only show when Yes (1) */}
                              {String(editPossessionText) === '1' && (
                                <div>
                                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">
                                    Date of Taking Over Possession
                                  </label>
                                  <input
                                    type="date"
                                    value={editPossessionDate}
                                    onChange={e => setEditPossessionDate(e.target.value)}
                                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all text-gray-800"
                                    style={{ fontSize: '13px' }}
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            /* All other rows — textarea */
                            <textarea
                              rows={idx === 7 ? 4 : 2}
                              value={editDetails[idx]}
                              onChange={e => handleDetailChange(idx, e.target.value)}
                              placeholder="Enter details..."
                              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all resize-none bg-white hover:border-gray-300 text-gray-800"
                              style={{ fontSize: '13px' }}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Signature row */}
                  <tr>
                    <td colSpan="2" className="border border-gray-200 px-4 py-6 bg-gray-50/40"></td>
                    <td className="border border-gray-200 px-4 py-6">
                      <div className="text-center">
                        <div className="mb-8"></div>
                        <div className="font-semibold text-gray-700 text-sm">Asst. Executive Engineer, (Construction),</div>
                        <div className="font-semibold text-gray-700 text-sm">South Central Railway, Akola.</div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={closeEdit}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 transform hover:scale-105"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 flex items-center justify-center space-x-3 shadow-lg"
              >
                {saving && <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />}
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>
  );

  // ── Main Render ────────────────────────────────────────────────────────
  return (
    <div className="max-w-4xl mx-auto p-6">
      {isEditing && renderEditModal()}

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 mb-6">
        <button
          onClick={openEdit}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
        >
          <Edit size={20} /> Edit
        </button>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-lg"
        >
          <Printer size={20} /> Print
        </button>
      </div>

      {loading && (
        <div className="text-center py-4 text-gray-400 text-sm animate-pulse">Loading data...</div>
      )}

      {/* Document Preview */}
      <div className="bg-white shadow-2xl rounded-lg overflow-hidden">
        <div
          ref={documentRef}
          className="document-container"
          style={{ maxWidth: '100%', margin: '0 auto', padding: '40px' }}
        >
          <div className="text-center text-xl font-bold mb-6 header">Annexure E</div>

          <table className="w-full border-collapse border border-gray-800">
            <thead>
              <tr className="bg-gray-300">
                <th className="border border-gray-800 p-2 text-center w-16">No.</th>
                <th className="border border-gray-800 p-2">Name of the work</th>
                {/* col3 header — static display only */}
                <th className="border border-gray-800 p-2">
                  {workName || 'Proposed gauge conversion from MG to BG between KHANDWA-AKOLA station.'}
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Row 1 — order_no + date */}
              <tr>
                <td className="border border-gray-800 p-2 text-center align-top">1</td>
                <td className="border border-gray-800 p-2 align-top">{PARTICULARS[0]}</td>
                <td className="border border-gray-800 p-2 align-top">
                  <div className="two-fields" style={{ display: 'flex', gap: '12px' }}>
                    {orderNo && <span>{orderNo}</span>}
                    {orderDate && <span>{toDisplayDate(orderDate)}</span>}
                  </div>
                </td>
              </tr>

              {/* Rows 2–8 — row 4 shows possession text + date */}
              {PARTICULARS.slice(1).map((label, i) => {
                const idx = i + 1;
                return (
                  <tr key={idx}>
                    <td className="border border-gray-800 p-2 text-center align-top">{idx + 1}</td>
                    <td className="border border-gray-800 p-2 align-top">{label}</td>
                    <td className="border border-gray-800 p-2 align-top">
                      {idx === 3 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span>{toDisplayDate(possessionDate)}</span>
                        </div>
                      ) : (
                        rowDetails[idx]
                      )}
                    </td>
                  </tr>
                );
              })}

              {/* Signature Row */}
              <tr>
                <td colSpan="2" className="border border-gray-800 p-4"></td>
                <td className="border border-gray-800 p-4 align-bottom">
                  <div className="text-center sign">
                    <div className="mb-16"></div>
                    <div className="font-bold">Asst. Executive Engineer, (Construction),</div>
                    <div className="font-bold">South Central Railway, Akola.</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AnnexureE;